// ─── Leave balance maths, shared by the Calendar (row badges, hover card,
// CSV exports) and the employee profile's Allowance section, so every screen
// shows the same Entitled / Used / Left figures. ───
import { format, parseISO, isSameDay, isWeekend } from 'date-fns';
import apiService from '../services/api';

// ─── Request dates arrive either as 'yyyy-MM-dd' or as full ISO timestamps.
// Only the date part matters: parsing e.g. '2026-06-05T23:30:00Z' as a
// timestamp in BST rolls it into the next day and counts an extra day. ───
export function toDay(value) {
  if (!value) return null;
  const d = parseISO(String(value).slice(0, 10));
  return isNaN(d) ? null : d;
}

// API responses come back either wrapped ({ data: … }) or bare. A wrapper
// whose data is null means "no record", so never treat the wrapper itself
// as the record (that silently fell back to the 116h default before).
export function unwrap(res) {
  if (res && typeof res === 'object' && !Array.isArray(res) && 'data' in res) return res.data;
  return res ?? null;
}

export function parseWorkDays(sched) {
  if (!sched) return null;
  let wd = sched.days ?? sched.workDays ?? sched.work_days;
  if (wd === undefined || wd === null) return null;
  if (typeof wd === 'string') {
    try { wd = JSON.parse(wd); } catch { return null; }
  }
  if (!Array.isArray(wd) || wd.length < 7) return null;
  return wd.map(v => v === true || v === 1 || v === '1' || v === 'true');
}

export function getActiveSchedule(empSchedules, day) {
  if (!empSchedules?.length) return null;
  const dateStr = format(day, 'yyyy-MM-dd');
  const getFrom = s =>
    (s.effectiveFrom ?? s.effective_from ?? s.from ?? '1970-01-01').slice(0, 10);
  const past = empSchedules.filter(s => getFrom(s) <= dateStr);
  if (past.length) return past.sort((a, b) => getFrom(b).localeCompare(getFrom(a)))[0];
  return [...empSchedules].sort((a, b) => getFrom(a).localeCompare(getFrom(b)))[0];
}

export function isNonWorkingDay(empSchedules, day) {
  if (!empSchedules?.length) return false;
  const sched    = getActiveSchedule(empSchedules, day);
  const workDays = parseWorkDays(sched);
  if (!workDays) return false;
  const jsDay    = day.getDay();
  const schedIdx = jsDay === 0 ? 6 : jsDay - 1;
  return !workDays[schedIdx];
}

export function getHoursPerDay(empSchedules, day) {
  const sched = getActiveSchedule(empSchedules, day);
  const hpd   = parseFloat(sched?.hoursPerDay ?? sched?.hours_per_day);
  return !isNaN(hpd) && hpd > 0 ? hpd : FULL_DAY_HOURS;
}

export function getPublicHoliday(publicHolidays, day) {
  return publicHolidays.find(h => {
    try { return isSameDay(parseISO(String(h.date).slice(0, 10)), day); }
    catch { return false; }
  }) ?? null;
}

export function empObservesHolidays(allowance) {
  // Public holidays don't apply to this person (opted out, or their policy has none)
  // Otherwise public holidays are always days off and never deducted.
  return !(allowance?.observesPublicHolidays === false || allowance?.balance?.observesPublicHolidays === false);
}

// ─── The public holidays that belong to one person: their holiday location's
// (holidays without a location belong to the company's default location),
// minus any they've been excluded from. Without location details on the
// allowance (older API) every company holiday is kept, as before. ───
export function holidaysForEmp(publicHolidays, allowance, empId) {
  const list = Array.isArray(publicHolidays) ? publicHolidays : [];
  const loc       = allowance?.holidayLocationId ?? null;
  const isDefault = allowance?.holidayLocationIsDefault;
  return list.filter(h => {
    if (loc) {
      const hLoc = h?.holidayLocationId ?? null;
      if (hLoc ? hLoc !== loc : isDefault === false) return false;
    }
    if (empId && Array.isArray(h?.excludedUsers) && h.excludedUsers.some(u => u?.id === empId)) return false;
    return true;
  });
}

// ─── South Africa Team members don't have a leave allowance/balance
// tracked in this system at all — leave requests are recorded for
// visibility on the calendar, but never deduct days/hours and never get
// a total (Entitled) figure assigned. The employee objects from
// getCompanyUsers() carry department info as a `departments` array
// (each entry has `departmentName`), not a flat `dept` string — this
// checks that shape first, with a few flat-field fallbacks for safety. ───
export function isSouthAfricaTeam(emp) {
  const deptNames = [
    emp?.dept,
    emp?.department,
    emp?.department?.name,
    emp?.team,
    emp?.team?.name,
    ...(Array.isArray(emp?.departments) ? emp.departments.map(d => d?.departmentName ?? d?.name) : []),
  ];
  return deptNames.some(v => typeof v === 'string' && v.toLowerCase().includes('south africa'));
}

export const HALF_DAY_AM_HOURS = 3;
export const HALF_DAY_PM_HOURS = 4.5;
export const FULL_DAY_HOURS    = 7.5;

// ─── Shared number formatting for balances, so the row badge, the hover
// card and the exports all show the same figure. Negative values are kept
// (an overdrawn balance should look overdrawn, not "0d"). ───
export function fmtHours(h) {
  const v = Math.round((Number(h) || 0) * 10) / 10;
  return `${v}h`;
}
export function fmtDays(d) {
  const n     = Number(d) || 0;
  const sign  = n < 0 ? '-' : '';
  const abs   = Math.round(Math.abs(n) * 10) / 10;
  const whole = Math.floor(abs);
  const frac  = Math.round((abs - whole) * 10) / 10;
  if (frac === 0)   return `${sign}${whole}d`;
  if (frac === 0.5) return `${sign}${whole > 0 ? whole : ''}\u00BDd`;
  return `${sign}${abs}d`;
}

// ─── Shared per-request deduction math. Used for balance totals, the hover
// card's mini calendar / history list, and the CSV exports.
//   opts.publicHolidays / opts.observesPH — bank holidays inside the request
//     aren't deducted for people who get them off.
//   opts.rangeStart / opts.rangeEnd — only the part of the request inside
//     this window counts (e.g. a Christmas request that runs into January
//     only uses this year's days from this year's allowance).
// A full day deducts that day's scheduled hours, not a flat 7.5h. ───
export function deductionForRequest(r, empScheds, opts = {}) {
  const { publicHolidays = [], observesPH = false, rangeStart = null, rangeEnd = null } = opts;
  const from = toDay(r.from);
  const to   = toDay(r.to ?? r.from) ?? from;
  if (!from || !to) return { hours: 0, days: 0 };
  if ((rangeStart && to < rangeStart) || (rangeEnd && from > rangeEnd)) return { hours: 0, days: 0 };

  if (isHalfDayLeave(r)) {
    // Nothing on a non-working day or one of their public holidays
    if (isWeekend(from) || isNonWorkingDay(empScheds, from) || (observesPH && !!getPublicHoliday(publicHolidays, from))) {
      return { hours: 0, days: 0 };
    }
    const stored = parseFloat(r.hours);
    if (!isNaN(stored)) return { hours: stored, days: 0.5 };
    const hpd    = getHoursPerDay(empScheds, from);
    const period = getHalfDayPeriod(r);
    const hours  = hpd === FULL_DAY_HOURS
      ? (period === 'PM' ? HALF_DAY_PM_HOURS : HALF_DAY_AM_HOURS)
      : hpd / 2;
    return { hours, days: 0.5 };
  }

  // Custom hours: the hours booked (nothing on a non-working day or public holiday)
  if (String(r.period ?? '').toLowerCase() === 'custom') {
    if ((rangeStart && from < rangeStart) || (rangeEnd && from > rangeEnd)) return { hours: 0, days: 0 };
    const off = isWeekend(from) || isNonWorkingDay(empScheds, from) || (observesPH && !!getPublicHoliday(publicHolidays, from));
    const h = parseFloat(r.hours);
    return off || isNaN(h) ? { hours: 0, days: 0 } : { hours: h, days: 0 };
  }

  // Full days are always counted from the dates, so non-working days and the
  // person's public holidays never count — even on requests stored before
  // that rule (their stored figure may include them)
  let hours = 0, days = 0;
  const cur  = new Date(Math.max(from, rangeStart ?? from));
  const last = new Date(Math.min(to, rangeEnd ?? to));
  while (cur <= last) {
    const isBankHoliday = observesPH && !!getPublicHoliday(publicHolidays, cur);
    if (!isWeekend(cur) && !isBankHoliday && !isNonWorkingDay(empScheds, cur)) {
      hours += getHoursPerDay(empScheds, cur);
      days  += 1;
    }
    cur.setDate(cur.getDate() + 1);
  }
  return { hours, days };
}

// ─── Shared leave-stats engine. Both the row badges and the admin hover
// card read from this single source, so the two views can never drift
// apart the way Dashboard/Calendar once did. ───
export function computeEmployeeLeaveStats(emp, requests, allowancesMap, schedulesMap, leaveColorsMap = {}, year, allHolidays = []) {
  const rec         = allowancesMap?.[emp.id] ?? null;
  // Only this person's holiday location's public holidays
  const publicHolidays = holidaysForEmp(allHolidays, rec, emp.id);
  const displayUnit = rec?.displayUnit ?? rec?.display_unit ?? 'days';
  const empScheds   = schedulesMap?.[emp.id] ?? [];
  const empId       = emp.id;
  const observesPublicHolidays = empObservesHolidays(rec);

  // The leave year this balance is for
  const yearNum    = year ? Number(year) : null;
  const rangeStart = yearNum ? new Date(yearNum, 0, 1) : null;
  const rangeEnd   = yearNum ? new Date(yearNum, 11, 31, 23, 59, 59) : null;
  const yearStart  = yearNum ? `${yearNum}-01-01` : null;
  const yearEnd    = yearNum ? `${yearNum}-12-31` : null;

  // Hours in a working day, from the schedule in force today (or at the
  // edge of the year being viewed, for past / future years)
  const today  = new Date();
  const refDay = !yearNum || today.getFullYear() === yearNum
    ? today
    : today.getFullYear() > yearNum ? new Date(yearNum, 11, 31) : new Date(yearNum, 0, 1);
  const defaultHpd = getHoursPerDay(empScheds, refDay);

  const deductOpts = { publicHolidays, observesPH: observesPublicHolidays, rangeStart, rangeEnd };

  function isDeductible(r) {
    const typeName = r.type ?? r.leaveType?.name;
    if (!typeName) return true;
    const cfg = leaveColorsMap[typeName];
    return cfg ? cfg.affectsBalance !== false : true;
  }

  // Every request that touches this year (including ones that cross into
  // it from December or out of it into January)
  const empReqs = requests.filter(r => {
    if ((r.empId ?? r.userId) !== empId) return false;
    if (!yearNum) return true;
    const from = String(r.from ?? '').slice(0, 10);
    const to   = String(r.to ?? r.from ?? '').slice(0, 10);
    return from <= yearEnd && to >= yearStart;
  });

  // ─── Server-calculated balance (leave engine) — the source of truth when
  // the allowance response carries one. It knows the person's leave policy:
  // accrual, part-time pro-rating, carry-over and expiry, the company's leave
  // year or their own 12-month cycle, and which public holidays apply. ───
  const server = rec?.balance;
  if (server && typeof server === 'object') {
    const hpd = Number(server.hoursPerDay) || defaultHpd;
    // Shown as days off and never deducted — unless public holidays don't apply to them at all
    const observes = server.observesPublicHolidays !== false;
    const serverDeductOpts = { ...deductOpts, observesPH: observes };
    if (server.noBalanceTracking) {
      return {
        displayUnit: server.unit ?? displayUnit, totalHours: null, totalDays: null, defaultHpd: hpd,
        used:    { hours: 0, days: 0 },
        pending: { hours: 0, days: 0 },
        remainingHours: null, remainingDays: null,
        empReqs, empScheds, deductOpts: serverDeductOpts,
        observesPublicHolidays: observes,
        noBalanceTracking: true,
        period: server.period ?? null,
      };
    }
    return {
      displayUnit:    server.unit ?? displayUnit,
      totalHours:     server.total?.hours ?? 0,
      totalDays:      server.total?.days ?? 0,
      defaultHpd:     hpd,
      used:           server.used ?? { hours: 0, days: 0 },
      pending:        server.pending ?? { hours: 0, days: 0 },
      remainingHours: server.available?.hours ?? 0,
      remainingDays:  server.available?.days ?? 0,
      empReqs, empScheds,
      deductOpts:     serverDeductOpts,
      observesPublicHolidays: observes,
      noBalanceTracking: false,
      period:         server.period ?? null,   // { start, end, leaveYear, label }
      accrued:        server.accrued ?? null,  // accruing policies: built up so far
      projected:      server.projected ?? null,
      serverBalance:  server,
    };
  }

  // ─── South Africa Team — skip allowance/deduction math entirely.
  // No total is assigned and nothing is ever deducted, regardless of
  // leave type. empReqs/empScheds/observesPublicHolidays are still
  // returned so the mini calendar and history list keep working. ───
  if (isSouthAfricaTeam(emp)) {
    return {
      displayUnit, totalHours: null, totalDays: null, defaultHpd,
      used:    { hours: 0, days: 0 },
      pending: { hours: 0, days: 0 },
      remainingHours: null, remainingDays: null,
      empReqs, empScheds, deductOpts,
      observesPublicHolidays,
      noBalanceTracking: true,
    };
  }

  const num = v => { const n = parseFloat(v); return isNaN(n) ? null : n; };
  const totalHours =
    (num(rec?.allowanceHours    ?? rec?.allowance_hours)     ?? 116) +
    (num(rec?.carryForwardHours ?? rec?.carry_forward_hours) ??   0) +
    (num(rec?.timeInLieuHours   ?? rec?.time_in_lieu_hours)  ??   0);

  function countDeductions(reqs) {
    return reqs.reduce((acc, r) => {
      const { hours, days } = deductionForRequest(r, empScheds, deductOpts);
      return { hours: acc.hours + hours, days: acc.days + days };
    }, { hours: 0, days: 0 });
  }

  const used    = countDeductions(empReqs.filter(r => r.status === 'approved' && isDeductible(r)));
  const pending = countDeductions(empReqs.filter(r => r.status === 'pending'  && isDeductible(r)));

  const totalDays      = totalHours / defaultHpd;
  const remainingHours = totalHours - used.hours;
  const remainingDays  = totalDays  - used.days;

  return {
    displayUnit, totalHours, totalDays, defaultHpd,
    used, pending, remainingHours, remainingDays,
    empReqs,          // every request for this employee/year, all statuses — hover card filters as needed
    empScheds,        // exposed so the hover card's mini calendar can shade non-working days
    deductOpts,       // so per-request figures elsewhere use the same rules as the totals
    observesPublicHolidays, // exposed so the mini calendar knows whether to shade bank holidays
    noBalanceTracking: false,
  };
}

// Row badge text, in whichever unit the person's allowance is tracked in
export function balanceLabels(stats) {
  if (!stats || stats.noBalanceTracking) return { remainingLabel: null, pendingLabel: null };
  if (stats.displayUnit === 'hours') {
    return {
      remainingLabel: fmtHours(stats.remainingHours),
      pendingLabel:   stats.pending.hours > 0 ? fmtHours(stats.pending.hours) : null,
    };
  }
  return {
    remainingLabel: fmtDays(stats.remainingDays),
    pendingLabel:   stats.pending.days > 0 ? fmtDays(stats.pending.days) : null,
  };
}

export function normaliseSchedules(raw) {
  let data = raw?.data ?? raw;
  if (!data) return [];
  if (!Array.isArray(data)) data = [data];
  return data.filter(Boolean);
}

export function isHalfDayLeave(leave) {
  if (!leave) return false;
  if (leave.halfDay === true || leave.isHalfDay === true) return true;
  if (leave.halfDayPeriod && leave.halfDayPeriod !== null) return true;
  if (leave.days !== undefined && parseFloat(leave.days) === 0.5) return true;
  return false;
}

export function getHalfDayPeriod(leave) {
  const period = leave?.halfDayPeriod ?? leave?.period ?? null;
  if (!period) return null;
  const p = String(period).toLowerCase();
  if (p === 'am' || p === 'morning') return 'AM';
  if (p === 'pm' || p === 'afternoon') return 'PM';
  return null;
}
// Leave types as { [name]: { affectsBalance } }, the shape
// computeEmployeeLeaveStats expects for deciding what comes off the balance
export function leaveTypeRules(types) {
  return (Array.isArray(types) ? types : []).reduce((acc, t) => {
    if (t?.name) acc[t.name] = { affectsBalance: t.affectsBalance !== false };
    return acc;
  }, {});
}

// ─── Fetch everything one person's balance needs for a leave year and
// work it out with the same rules as the Calendar. Used by the profile's
// Allowance section. Returns the stats plus the raw allowance record. ───
export async function loadEmployeeLeaveStats(emp, year) {
  const [allowanceRes, schedulesRes, requestsRes, typesRes, holidaysRes] = await Promise.all([
    apiService.getEmployeeAllowance(emp.id, year).catch(() => null),
    apiService.getEmployeeSchedules(emp.id).catch(() => null),
    apiService.getLeaveRequests({
      scope: 'all',
      limit: 1000,
      from: `${year}-01-01`,
      to:   `${year}-12-31`,
    }).catch(() => null),
    apiService.getLeaveTypes().catch(() => null),
    apiService.getPublicHolidays(year).catch(() => null),
  ]);

  const allowance = unwrap(allowanceRes);
  const schedules = normaliseSchedules(schedulesRes);
  const reqList   = requestsRes?.data?.requests ?? requestsRes?.data ?? requestsRes ?? [];
  const types     = unwrap(typesRes);
  const holidays  = unwrap(holidaysRes);

  const stats = computeEmployeeLeaveStats(
    emp,
    Array.isArray(reqList) ? reqList : [],
    allowance && typeof allowance === 'object' ? { [emp.id]: allowance } : {},
    { [emp.id]: schedules },
    leaveTypeRules(types),
    year,
    Array.isArray(holidays) ? holidays : []
  );
  return { stats, allowance: allowance && typeof allowance === 'object' ? allowance : null };
}